import { useId } from 'react';

import { rangeClasses } from '@/components/bedrock';

import type { ReactElement } from 'react';

/**
 * A labelled percentage slider for the usage calculator's two posted-price flags
 * — the share of interactions that take an action, and the share that look
 * something up (ADR-0442).
 *
 * It replaces the retired light/typical/heavy segmented control. Under posted
 * pricing there is no "typical interaction weight" to pick from a menu: a buyer
 * describes their own traffic in the same two terms the price is built from, and
 * the total is exact. A preset would be a guess dressed as a fact, which is the
 * hedge the model exists to remove.
 *
 * Renders a `data-share={name}` handle so callers and tests can target it
 * unambiguously. Keyboard-reachable by construction (a native `range` input, so
 * arrows / Home / End / Page keys all work); the value is announced via
 * `aria-valuetext` in the reader's own locale, because "35" alone does not say
 * what it is a share OF. RTL-safe: the native range input inherits `direction`
 * from `<html dir>`, and spacing uses logical Tailwind utilities only.
 */
interface ShareSliderProps {
  /** data-attribute value, e.g. "acted" → `data-share="acted"`. */
  readonly name: string;
  readonly label: string;
  readonly help: string;
  /** Localized, formatted percentage shown beside the label (e.g. "25%"). */
  readonly displayValue: string;
  /** Full sentence announced to assistive tech in place of the bare number. */
  readonly valueText: string;
  /** Whole percent, 0–100. */
  readonly value: number;
  readonly onChange: (percent: number) => void;
}

/** Percent granularity of the slider — fine enough to describe real traffic. */
const SHARE_STEP = 5;

export function ShareSlider({
  name,
  label,
  help,
  displayValue,
  valueText,
  value,
  onChange,
}: ShareSliderProps): ReactElement {
  const inputId = useId();
  return (
    <div className="mt-6">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={inputId} className="text-sm font-medium text-[color:var(--bd-ink)]">
          {label}
        </label>
        <span className="text-sm font-semibold tabular-nums text-[color:var(--bd-ink)]">
          {displayValue}
        </span>
      </div>
      <input
        id={inputId}
        data-share={name}
        type="range"
        min={0}
        max={100}
        step={SHARE_STEP}
        value={value}
        aria-valuetext={valueText}
        onChange={(e) => {
          onChange(Number(e.target.value));
        }}
        className={rangeClasses({ className: 'mt-2' })}
      />
      <p className="mt-2 text-xs text-[color:var(--bd-muted)]">{help}</p>
    </div>
  );
}
